/**
 * Экран «Входящие» (`/inbox`, ТЗ §7 и §8.6): все `inbox_items` клиники со статусом,
 * источником, датой поступления, заявителем и ссылками на заведённые дела.
 *
 * «Повторить» — для `failed`, рядом с текстом ошибки; «Разобрать» — для `needs_confirm`.
 * Незавершённые входящие раскрываются модалкой с ходом разбора.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { InboxItemDto, InboxStatus, Source } from '@/api/types';
import { useInbox, useRetryInbox } from '@/api/hooks';
import { errorMessage, FormError, LoadingState } from '@/components/ErrorState';
import { Modal, useOpenCase } from '@/components/Modal';
import { useToast } from '@/components/Toast';
import { appealWord, fmtDateTime } from '@/lib/format';
import { INBOX_STATUS_LABELS, SOURCE_LABELS, sourceLabel } from '@/lib/stages';
import InboxTable from '@/components/inbox/InboxTable';
import IngestProgress from '@/components/inbox/IngestProgress';

const PAGE_LIMIT = 25;

const STATUS_FILTERS: ReadonlyArray<{ value: '' | InboxStatus; label: string }> = [
    { value: '', label: 'Все' },
    { value: 'new', label: INBOX_STATUS_LABELS.new },
    { value: 'processing', label: INBOX_STATUS_LABELS.processing },
    { value: 'needs_confirm', label: INBOX_STATUS_LABELS.needs_confirm },
    { value: 'done', label: INBOX_STATUS_LABELS.done },
    { value: 'failed', label: INBOX_STATUS_LABELS.failed },
];

export default function InboxPage() {
    const openCase = useOpenCase();
    const toast = useToast();

    const [search, setSearch] = useState('');
    const [q, setQ] = useState('');
    const [status, setStatus] = useState<'' | InboxStatus>('');
    const [source, setSource] = useState<'' | Source>('');
    const [page, setPage] = useState(1);
    const [watched, setWatched] = useState<InboxItemDto | null>(null);

    useEffect(() => {
        const timer = window.setTimeout(() => {
            setQ(search.trim());
            setPage(1);
        }, 350);
        return () => window.clearTimeout(timer);
    }, [search]);

    const list = useInbox(
        {
            status: status || undefined,
            source: source || undefined,
            q: q || undefined,
            page,
            limit: PAGE_LIMIT,
        },
        // Разбор идёт в фоне: список должен оживать сам, не требуя перезагрузки.
        { refetchInterval: 10_000 },
    );

    const retry = useRetryInbox();

    const items = list.data?.items ?? [];
    const total = list.data?.total ?? 0;
    const pages = list.data?.pages ?? 1;
    const filtered = Boolean(q || status || source);

    const resetFilters = () => {
        setSearch('');
        setQ('');
        setStatus('');
        setSource('');
        setPage(1);
    };

    const onRetry = (item: InboxItemDto) => {
        retry.mutate(item.id, {
            onSuccess: () => toast('Обращение отправлено в разбор повторно'),
            onError: (error) => toast(errorMessage(error)),
        });
    };

    return (
        <>
            <div className="page-header">
                <h1>
                    📥 Входящие
                    <span>
                        {total} {appealWord(total)} во входящих
                    </span>
                </h1>
                <span className="update-info">🔄 статус обновляется каждые 10 секунд</span>
            </div>

            <div className="filter-bar">
                <input
                    type="search"
                    className="search-input"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Поиск по теме, заявителю или источнику"
                    aria-label="Поиск по входящим"
                />
                <span className="seg" role="group" aria-label="Статус обработки">
                    {STATUS_FILTERS.map((filter) => (
                        <button
                            key={filter.value || 'all'}
                            type="button"
                            className={status === filter.value ? 'active' : undefined}
                            onClick={() => {
                                setStatus(filter.value);
                                setPage(1);
                            }}
                        >
                            {filter.label}
                        </button>
                    ))}
                </span>
                <select
                    className="act-select"
                    value={source}
                    aria-label="Источник обращения"
                    onChange={(event) => {
                        setSource(event.target.value as '' | Source);
                        setPage(1);
                    }}
                >
                    <option value="">Все источники</option>
                    {Object.entries(SOURCE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                            {label}
                        </option>
                    ))}
                </select>
                {filtered ? (
                    <button type="button" className="search-clear" onClick={resetFilters}>
                        Сбросить
                    </button>
                ) : null}
                <Link className="act-btn primary" to="/inbox/new">
                    ＋ Добавить обращение
                </Link>
            </div>

            <div className="stat-block">
                <div className="block-header">
                    <span>📨 Обращения на разборе</span>
                    <span className="checklist-progress">
                        стр. {page} из {Math.max(pages, 1)}
                    </span>
                </div>
                <div className="block-body">
                    <FormError>{list.isError ? errorMessage(list.error) : null}</FormError>
                    {list.isPending ? (
                        <LoadingState variant="inline" label="Загружаем входящие…" />
                    ) : (
                        <InboxTable
                            items={items}
                            retryingId={retry.isPending ? (retry.variables ?? null) : null}
                            onOpenCase={openCase}
                            onResolve={(item) => setWatched(item)}
                            onWatch={(item) => setWatched(item)}
                            onRetry={onRetry}
                        />
                    )}
                </div>
                <div className="totals-row">
                    <span>
                        Показано <b>{items.length}</b> из <b>{total}</b>
                        {filtered ? ' (с учётом фильтров)' : ''}
                    </span>
                    <span>
                        <button
                            type="button"
                            className="act-btn"
                            disabled={page <= 1}
                            onClick={() => setPage((value) => Math.max(1, value - 1))}
                        >
                            ← Назад
                        </button>{' '}
                        <button
                            type="button"
                            className="act-btn"
                            disabled={page >= pages}
                            onClick={() => setPage((value) => value + 1)}
                        >
                            Вперёд →
                        </button>
                    </span>
                </div>
            </div>

            {watched ? (
                <Modal
                    title={watched.subject?.trim() || 'Без темы'}
                    idLine={`${fmtDateTime(watched.received_at)} · ${sourceLabel(watched.source)}`}
                    chips={
                        watched.applicant_name || watched.applicant_contact ? (
                            <>
                                {watched.applicant_name ? <span>{watched.applicant_name}</span> : null}
                                {watched.applicant_contact ? <span>{watched.applicant_contact}</span> : null}
                            </>
                        ) : null
                    }
                    onClose={() => setWatched(null)}
                >
                    <IngestProgress
                        inboxId={watched.id}
                        onOpenCase={(caseId) => {
                            setWatched(null);
                            openCase(caseId);
                        }}
                        onClose={() => setWatched(null)}
                    />
                </Modal>
            ) : null}
        </>
    );
}
